import React, { useState } from 'react';
import { Button, Card, Space, Table } from 'antd';
import api from '@services/api';
import { useParams } from 'react-router-dom';
import { DeviceTypeSettings, GroupMapList, GroupMapListVersion, StbGroupExtended } from '@services/types';
import { toast } from 'react-toastify';
import { downloadFile } from '@services/utils';

const { Column } = Table;

const GroupMap = () => {
  const { id } = useParams<{ id: string }>();
  const [stbGroup, setStbGroup] = React.useState<StbGroupExtended>();
  const [mapResponse, setMapResponse] = React.useState<GroupMapList[]>([]);
  const [addRow, setAddRow] = React.useState<GroupMapList['count']>();
  const [deviceSetting, setDeviceSetting] = useState<DeviceTypeSettings>();
  const [versionData, setVersionData] = React.useState<GroupMapListVersion['counters']>();

  React.useEffect(() => {
    if (id) {
      Promise.all([api.stbGroups.getStbGroupsById(id), api.reports.getGroupMap({ stbGroupId: id })]).then(
        ([stbGroups, mapData]) => {
          setStbGroup(stbGroups);
          setMapResponse(mapData.groupMapEntries);
        },
      );
    }
  }, [id]);

  React.useEffect(() => {
    if (stbGroup?.deviceTypeId) {
      api.administration.getDeviceTypeSettings(stbGroup.deviceTypeId).then((item) => {
        setDeviceSetting(item);
      });
    }
  }, [stbGroup?.deviceTypeId]);

  const AddColumn = (i: GroupMapList) => {
    if (i && id) {
      api.reports
        .connectColumn({
          stbGroupId: id,
          uiVersion: i.uiVersion,
          firmwareVersion: i.firmwareVersion,
          loaderVersion: i.loaderVersion,
        })
        .then((data) => {
          setVersionData(data.counters);
        });
    }
  };

  const obj = Object.assign(mapResponse || []);

  const exportGroupMap = async () => {
    try {
      const res = await api.reports.getExportFileGroup({
        stbGroupId: stbGroup?.id || '',
        deviceTypeId: stbGroup?.deviceTypeId || '',
      });
      if (res) {
        const filename = `groupMap-${stbGroup?.name}`;
        if (filename) {
          downloadFile(res, filename);
          toast.success('Group Map export started successfully.');
        }
      }
    } catch (error) {
      toast.error('Group Map isn`t exported');
    }
  };

  return (
    <Card
      title={`Group map for ${stbGroup?.name} `}
      style={{ marginBottom: '1rem' }}
      extra={
        <Space>
          <Button type="primary" onClick={() => exportGroupMap()}>
            CSV
          </Button>
        </Space>
      }
    >
      <Table dataSource={obj}>
        <Column
          align="center"
          title="Count"
          dataIndex="count"
          key="count"
          render={(_text, etc: GroupMapList) => etc.count}
        />
        <Column
          align="center"
          title="UI/APK"
          dataIndex="uiVersion"
          key="uiVersion"
          render={(_text, etc: GroupMapList) => etc.uiVersion}
        />
        <Column
          align="center"
          title="FW"
          dataIndex="firmwareVersion"
          key="firmwareVersion"
          render={(_text, etc: GroupMapList) => etc.firmwareVersion}
        />
        <Column
          align="center"
          title="LOADER"
          dataIndex="loaderVersion"
          key="loaderVersion"
          render={(_text, etc: GroupMapList) => etc.loaderVersion}
        />
        {deviceSetting?.fwExtraName1?.length && (
          <Column
            title={deviceSetting?.fwExtraName1}
            dataIndex="etc.customFirmware1Version"
            render={(_text, etc: GroupMapList) => etc.customFirmware1Version}
          />
        )}

        {deviceSetting?.fwExtraName2?.length && (
          <Column
            title={deviceSetting?.fwExtraName2}
            dataIndex="etc.customFirmware2Version"
            render={(_text, etc: GroupMapList) => etc.customFirmware2Version}
          />
        )}
        {deviceSetting?.fwExtraName3?.length && (
          <Column
            title={deviceSetting?.fwExtraName3}
            dataIndex="etc.customFirmware3Version"
            render={(_text, etc: GroupMapList) => etc.customFirmware3Version}
          />
        )}

        <Column
          align="center"
          title="Conn. last 24h"
          dataIndex="connectedLast24HCount"
          key="connectedLast24HCount"
          render={(_text, etc: GroupMapList) =>
            versionData && addRow === etc.count ? (
              versionData?.nbConnectedWithinLast24H
            ) : (
              <>
                <Button
                  onClick={() => {
                    setAddRow(etc?.count);
                  }}
                >
                  More
                </Button>
                {addRow && addRow === etc.count && AddColumn(etc)}
              </>
            )
          }
        />
        <Column
          align="center"
          title="Not conn. 1 month"
          dataIndex="notConnectedLast1MCount"
          key="notConnectedLast1MCount"
          render={(_text, etc: GroupMapList) =>
            versionData && addRow === etc.count ? versionData?.nbConnectionFailureCountLast1M : ''
          }
        />
        <Column
          align="center"
          title="Not conn. 3 month"
          dataIndex="notConnectedLast3MCount"
          key="notConnectedLast3MCount"
          render={(_text, etc: GroupMapList) =>
            versionData && addRow === etc.count ? versionData?.nbConnectionFailureCountLast3M : ''
          }
        />
        <Column
          align="center"
          title="Not conn. 6 month"
          dataIndex="notConnectedLast6MCount"
          key="notConnectedLast6MCount"
          render={(_text, etc: GroupMapList, index) =>
            versionData && addRow === etc.count ? versionData?.nbConnectionFailureCountLast6M : ''
          }
        />
      </Table>
    </Card>
  );
};

export default React.memo(GroupMap);
